/**
 * Flavor Picker
 * File: assets/flavor-picker.js          (NEW)
 *
 * ============================================================
 * ADDED 25 OCT 2026
 * ============================================================
 *
 * Switches which pack-size group is visible when a flavor is chosen, then
 * hands the resolved variant to the existing VariantSelection so every piece
 * of pricing, media and availability logic already in the theme runs
 * unchanged.
 *
 * WHY IT IS THIS SMALL
 *
 * sections/homepage-pdp.liquid renders a complete, fully priced set of pack
 * buttons for every flavor, and hides all but one. So a flavor change is a
 * visibility toggle plus one selectVariant() call. There is no price
 * rewriting, no re-pointing of variant ids, and nothing in
 * assets/variant-selection.js had to change.
 *
 * The alternative, three buttons whose variant id is rewritten on each flavor
 * change, needs this file to duplicate every formatting rule that already
 * exists in Liquid: money formatting, per-pack maths, the discount badge, the
 * free shipping flag. That is the cart-line mistake again, logic living in two
 * languages that have to agree.
 *
 * KEEPING THE SIZE ACROSS A FLAVOR CHANGE
 *
 * Someone looking at 12 Packs who clicks a new flavor wants 12 Packs of that
 * flavor, not whatever sits first. The current size is read off the selected
 * button and matched by value in the incoming group. If that pairing does not
 * exist or is sold out, the first available size in the new group is used
 * instead, because landing on a disabled row is worse than moving the choice.
 *
 * A forced fallback deliberately does NOT overwrite the remembered size. If
 * someone picks 36 Packs, switches to a flavor that has no 36 Packs and gets
 * dropped to the 3 Pack, then switches to a flavor that does have it, they get
 * 36 Packs back. The remembered value is the last size the person actually
 * chose, not the last one the page happened to land on. Updating it on
 * fallback would quietly discard their choice because of a stock gap in a
 * flavor they did not buy.
 */
(function () {
  "use strict";

  function init(root) {
    var pills = root.querySelector("[data-gg-flavors]");
    var groupWrap = root.querySelector("[data-gg-size-groups]");
    if (!pills || !groupWrap) return;

    var form =
      root.querySelector("[data-product-form]") ||
      document.querySelector("[data-product-form]");

    function groups() {
      return Array.prototype.slice.call(
        groupWrap.querySelectorAll("[data-gg-size-group]")
      );
    }

    /* The size currently showing, read from whichever button is styled as
       selected. Falls back to the first enabled button in the visible group. */
    function currentSize() {
      var visible = groups().filter(function (g) {
        return !g.hasAttribute("hidden");
      })[0];
      if (!visible) return null;

      var chosen = visible.querySelector("button.bg-gum[data-gg-size]");
      if (chosen) return chosen.getAttribute("data-gg-size");

      var first = visible.querySelector("button[data-gg-size]:not([disabled])");
      return first ? first.getAttribute("data-gg-size") : null;
    }

    function selectFlavor(flavor) {
      var target = null;

      groups().forEach(function (g) {
        var match = g.getAttribute("data-gg-size-group") === flavor;
        if (match) {
          g.removeAttribute("hidden");
          target = g;
        } else {
          g.setAttribute("hidden", "");
        }
      });
      if (!target) return;

      Array.prototype.forEach.call(
        pills.querySelectorAll("[data-gg-flavor]"),
        function (p) {
          var on = p.getAttribute("data-gg-flavor") === flavor;
          p.classList.toggle("is-selected", on);
          p.setAttribute("aria-pressed", on ? "true" : "false");
        }
      );

      /* Let the rest of the page react to the flavor itself, not just the
         variant. These two custom properties tint everything keyed to the
         current flavor; the CSS that consumes them is in critical.css.

           --gg-flavor-accent   pale tone, for fills that carry ink text
           --gg-flavor-dark     strong tone, for fills that carry white text

         Two tones because one cannot do both jobs. Every pale swatch is
         sampled off the packaging and none is dark enough for white text:
         #E0FA64 under white is 1.5:1.

         `root` here is the <section>, not documentElement. It is the same
         element Liquid writes both properties onto server-side, so the
         values are only ever overwritten in place, and a page that arrives
         on ?variant=<Blue Raspberry> is already blue before this runs.

         Both are read off the pill rather than recomputed, which keeps the
         theme editor as the single source for the palette. */
      var pill = pills.querySelector('[data-gg-flavor="' + cssEscape(flavor) + '"]');
      if (pill) {
        ["--gg-flavor-accent", "--gg-flavor-dark"].forEach(function (prop) {
          var value = pill.style.getPropertyValue(prop);
          if (value) root.style.setProperty(prop, value.trim());
        });

        /* The tagline under the heading. Read off the pill for the same
           reason the colors are: the theme editor stays the only place this
           copy exists, and nothing here needs a table of flavors.

           textContent, never innerHTML. This string comes from a section
           setting, so it is the client's to type, and the day someone writes
           an ampersand or an apostrophe it should render as an ampersand or
           an apostrophe rather than as markup. */
        var tagline = pill.getAttribute("data-gg-tagline") || "";
        Array.prototype.forEach.call(
          document.querySelectorAll("[data-gg-flavor-tagline]"),
          function (el) { el.textContent = tagline; }
        );
      }

      /* The gallery. Media is labelled by flavor in its alt text and filtered
         in assets/media-carousel.js; unlabelled media is shared and stays put.
         Done before selectVariant so the variant's own featured image, if it
         has one, lands on a gallery that is already showing the right set. */
      if (window.MediaCarousel && window.MediaCarousel.filter) {
        Array.prototype.forEach.call(
          document.querySelectorAll("[data-media-carousel]"),
          function (c) { window.MediaCarousel.filter(c, flavor); }
        );
      }

      /* Anything on the page that names the flavor, such as the badge above
         the product title. */
      Array.prototype.forEach.call(
        document.querySelectorAll("[data-gg-flavor-label]"),
        function (el) { el.textContent = flavor; }
      );

      var size = lastSize;
      var btn = null;
      if (size) {
        btn = target.querySelector(
          'button[data-gg-size="' + cssEscape(size) + '"]:not([disabled])'
        );
      }
      if (!btn) {
        btn = target.querySelector("button[data-gg-size]:not([disabled])");
      }
      if (!btn) return;

      var variantId = btn.getAttribute("data-variant-id");
      if (!variantId) return;

      if (window.VariantSelection && form) {
        window.VariantSelection.selectVariant(variantId, form);
      } else {
        /* VariantSelection is the only thing that styles the selected row, so
           without it the picker would look unresponsive. This keeps the page
           usable rather than silently dead. */
        var select = form && form.querySelector("[data-variant-select]");
        if (select) {
          select.value = variantId;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    }

    /* Remembered rather than read at click time: by the moment the new group
       is visible the old one is already hidden, and currentSize() would be
       reading the group we just switched to. */
    var lastSize = currentSize();

    groupWrap.addEventListener("click", function (e) {
      var b = e.target.closest ? e.target.closest("button[data-gg-size]") : null;
      if (b && !b.disabled) lastSize = b.getAttribute("data-gg-size");
    });

    pills.addEventListener("click", function (e) {
      var p = e.target.closest ? e.target.closest("[data-gg-flavor]") : null;
      if (!p || p.disabled) return;
      e.preventDefault();
      selectFlavor(p.getAttribute("data-gg-flavor"));
    });
  }

  /* Flavor names are author-controlled but go into attribute selectors, so
     they get escaped. CSS.escape is not in every browser the store sees. */
  function cssEscape(v) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(v);
    return String(v).replace(/["\\]/g, "\\$&");
  }

  function boot() {
    Array.prototype.forEach.call(
      document.querySelectorAll("[data-gg-size-groups]"),
      function (wrap) {
        var root = wrap.closest("section, .shopify-section") || document.body;
        init(root);
      }
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.FlavorPicker = { init: init };
})();